import { Component } from "react";

import { Divider, Grid } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import {
  CUSTOM_FIELD_TYPE,
  IMetaExpression,
  META_EXPRESSION_SCOPE,
} from "fieldpro-tools/dist/src/types";
import { IAccessRightProfile } from "fieldpro-tools/src/types";
import _ from "lodash";
import { connect } from "react-redux";

import { getLang } from "containers/authentication/redux/selector";
import {
  getMobileProfiles,
  getSelectedClient,
} from "containers/clients/redux/selectors";
import { allListsSelector } from "containers/lists/redux/selectors";
import { getAllActivities } from "containers/workflows/redux/selectors";
import { getAllWebhooks } from "containers/workflows/redux/webhooks/selectors";
import {
  getJobsFromType,
  TAnyJob,
} from "containers/workflows/subcategories/jobs/Jobscreen/utils";
import { allJobsComposedSelector } from "containers/workflows/subcategories/jobs/redux/selectors";
import { IFormState } from "hooks/useFormState";
import * as l from "lang";
import TLang from "model/application/Lang";
import { ICreateEditModalProps } from "model/application/modal/CreateEditModal";
import { IClient } from "model/entities/Client";
import { TExpressionType } from "model/entities/Expression";
import { ITransformation, STEP_TYPE } from "model/entities/Job";
import { IList } from "model/entities/List";
import { ITrigger } from "model/entities/Trigger";
import { IWebhook } from "model/entities/Webhook";
import { IActivity, IWorkflow } from "model/entities/Workflow";
import IRootState from "redux/store/model";
import { activityNeedsCustomer } from "utils/businessUtils";
import { getResourceIdForScope } from "utils/metaExpressions/getResourceIdForScope";
import { mergeCurrentResource } from "utils/metaExpressions/mergeCurrentResource";

import { stepTypeToMeScope } from "../../../Input/InputMetaExpression/utils/stepTypeToMeScope";
import MetaExpressionStepper from "../MetaExpressionStepper";
import { MetaExpressionWizardFirstStep } from "./MetaExpressionWizardFirstStep";
import { MetaExpressionWizardFourthStep } from "./MetaExpressionWizardFourthStep";
import { MetaExpressionWizardSecondStep } from "./MetaExpressionWizardSecondStep";
import { MetaExpressionWizardThirdStep } from "./MetaExpressionWizardThirdStep";

const styles = () => ({
  stepLabel: {
    color: "green",
  },
});

export type IMetaExpressionWizardAttributes = IMetaExpression;

export interface IMetaExpressionWizardState extends IMetaExpression {
  _error?: any;
  useSecondDefaultFields: boolean;
  subscope: META_EXPRESSION_SCOPE | undefined;
  currentStepId?: string;
}

/**
 * Identifies the UI feature area from which a meta expression is being built.
 * Used by getAllowedMetaExpressionScopes to determine which META_EXPRESSION_SCOPE's are valid in that context.
 */
export enum CALLED_FROM {
  LIST = "LIST",
  LIST_ITEM_NAME_TEMPLATE = "LIST_ITEM_NAME_TEMPLATE",
  ACTIVITY = "ACTIVITY",
  ACTIVITY_OPERATION = "ACTIVITY_OPERATION",
  ACTIVITY_OPERATION_MATRIX_CELL = "ACTIVITY_OPERATION_MATRIX_CELL",
  /**
   * Items restriction conditions on SCOL/MCOL/MXOL questions in an activity. Adds ITEM scope
   * so the condition can reference the candidate list item being filtered.
   */
  ACTIVITY_ITEMS_RESTRICTIONS = "ACTIVITY_ITEMS_RESTRICTIONS",
  JOB = "JOB",
  JOB_NOTIFICATION = "JOB_NOTIFICATION",
  JOB_TRANSFORMATION = "JOB_TRANSFORMATION",
  WORKFLOW = "WORKFLOW",
  WORKFLOW_SETTINGS = "WORKFLOW_SETTINGS",
  CUSTOMER_TO_USE = "CUSTOMER_TO_USE",
  RECEIPT_HEADER = "RECEIPT_HEADER",
  /**
   * Used when the expression is built inside a matrix sub-question cell.
   * To be able to target the current item (ITEM_CELL scope) or the current answers (MATRIX_CELL scope¹).
   *
   * (¹same name but slightly different meaning)
   */
  MATRIX_CELL = "MATRIX_CELL",
  TRIGGER = "TRIGGER",
  CUSTOMER_PROMO_GROUP_CONDITION = "CUSTOMER_PROMO_GROUP_CONDITION",
}

export interface IMetaExpressionWizardAdditionnalProps {
  currentWorkflow?: IWorkflow; // possibly current object in manipulation. Not possible to find it in the store
  currentActivity?: IActivity; // possibly current object in manipulation. Not possible to find it in the store
  currentList?: IList; // possibly current object in manipulation. Not possible to find it in the store
  currentTrigger?: ITrigger;
  matrixTag?: string; // only if we are scoped "ITEM_CELL" or "MATRIX_CELL"
  expressionType?: TExpressionType; // small changes in the options if it's an operation (number only) or a condition
  calledFrom?: CALLED_FROM; // the original place where the input meta expression was called
  type?: CUSTOM_FIELD_TYPE;
  bindedInteg?: ITransformation;
}

export interface IMetaExpressionWizardProps extends ICreateEditModalProps<
  IMetaExpressionWizardAdditionnalProps,
  IMetaExpression
> {
  activities: IActivity[];
  lists: IList[];
  jobs: TAnyJob[];
  webhooks: IWebhook[];
  currentClient: IClient;
  classes: any;
  numberIndex?: number;
  lang: TLang;
  mobileProfiles?: IAccessRightProfile[];
  formState: IFormState<IMetaExpression>;
  currentStep: number;
  useDefaultFields: boolean;
  setDefaultFields: (useDefaultFields: boolean) => void;
}

class MetaExpressionWizard extends Component<
  IMetaExpressionWizardProps,
  IMetaExpressionWizardState
> {
  constructor(props: IMetaExpressionWizardProps) {
    super(props);
    let currentStepId: undefined | string = undefined;
    let subscope: META_EXPRESSION_SCOPE | undefined;
    if (props.additionnalProps.currentWorkflow && props.attributes.step) {
      const stepSchema = props.additionnalProps.currentWorkflow.steps.find(
        (s) => s.id === props.attributes.step,
      );
      if (stepSchema) {
        currentStepId = stepSchema.schema_id;
        switch (stepSchema.type) {
          case STEP_TYPE.TRANSFORMATION: {
            subscope = META_EXPRESSION_SCOPE.TRANSFORMATION;
            break;
          }
          case STEP_TYPE.CREATE_ITEM:
          case STEP_TYPE.EDIT_ITEM:
          case STEP_TYPE.SCRIPT: {
            subscope = META_EXPRESSION_SCOPE.JOB_REPORT;
            break;
          }
        }
      }
    }
    this.state = {
      step: undefined,
      useSecondDefaultFields: false,
      subscope,
      currentStepId,
    };
  }

  /**
   * Handles input changes
   * @param {Object} e Event Object
   */
  handleInputChange = (value: any, name: string) => {
    const { handleInputChange: onChangeAttributeValues } = this.props.formState;
    const { setDefaultFields } = this.props;

    let newState: Partial<IMetaExpressionWizardState> = {};

    if (name === "custom_field1") {
      newState = {
        custom_field3: undefined,
        custom_field2: undefined,
        option_tag: undefined,
        field: undefined,
      };
    }

    if (name === "custom_field2") {
      newState = {
        custom_field3: undefined,
        option_tag: undefined,
        field: undefined,
      };
    }

    if (name === "scope") {
      newState = {
        custom_field1: undefined,
        custom_field2: undefined,
        custom_field3: undefined,
        option_tag: undefined,
        cell: undefined,
        field: undefined,
        step: undefined,
        currentStepId: undefined,
      };
      // special case if we select the integ mode: it's as if we selected the step also
      if (value === META_EXPRESSION_SCOPE.INTEG_RECORD) {
        newState.currentStepId = this.props.additionnalProps.bindedInteg?.id;
      }
    }

    if (name === "useDefaultFields") {
      newState = {
        custom_field1: undefined,
        custom_field2: undefined,
        custom_field3: undefined,
        option_tag: undefined,
        field: undefined,
      };
      setDefaultFields(value);
    }

    if (name === "useSecondDefaultFields") {
      newState = {
        custom_field2: undefined,
        custom_field3: undefined,
        option_tag: undefined,
        field: undefined,
      };
    }

    if (name === "step" && this.props.additionnalProps.currentWorkflow) {
      const selectedWorkflowStep = _.find(
        this.props.additionnalProps.currentWorkflow.steps,
        {
          id: value,
        },
      );
      if (selectedWorkflowStep) {
        const stepType = selectedWorkflowStep.type;
        switch (stepType) {
          case STEP_TYPE.ACTIVITY:
          case STEP_TYPE.TRANSFORMATION:
            newState = {
              currentStepId: selectedWorkflowStep.schema_id,
              custom_field1: undefined,
              custom_field2: undefined,
              custom_field3: undefined,
            };
            break;
          default:
            break;
        }
        newState.subscope = stepTypeToMeScope(stepType);
      }
    }
    const cleanValue = value || undefined; // replace "" with undefined so it is not sent to backend
    // @ts-expect-error TS7053
    newState[name] = cleanValue;

    this.setState({
      ...newState,
    } as IMetaExpressionWizardState);

    _.forEach(_.keys(newState), (key) => {
      // @ts-expect-error TS7053
      onChangeAttributeValues(newState[key], key);
    });
  };

  onChangeDefaultMEValue = (default_value: string) => {
    const { handleInputChange: onChangeAttributeValues } = this.props.formState;

    this.setState((state) => ({
      ...state,
      default_value,
    }));

    onChangeAttributeValues(default_value, "default_value");
  };

  buildStepper = () => {
    const {
      title,
      scope,
      field,
      step,
      custom_field1,
      custom_field2,
      option_tag,
      target_type,
      default_value,
    } = this.props.formState.attributes;

    const { currentStep, additionnalProps } = this.props;
    const currentWorkflowStep = _.find(
      additionnalProps.currentWorkflow?.steps,
      {
        id: step,
      },
    );
    return (
      <MetaExpressionStepper
        currentStep={currentStep}
        title={title}
        target_type={target_type}
        scope={scope}
        selectedStepForWorkflow={currentWorkflowStep}
        field={field}
        custom_field1={custom_field1}
        custom_field2={custom_field2}
        option_tag={option_tag}
        defaultValue={default_value}
      />
    );
  };

  render() {
    const { useSecondDefaultFields, subscope, currentStepId } = this.state;
    const {
      title,
      scope,
      field,
      step,
      custom_field1,
      custom_field2,
      custom_field3,
      option_tag,
      target_type,
      target_content,
      default_value,
      disabled,
      verified,
    } = this.props.formState.attributes;

    const { errors } = this.props.formState;

    const {
      lang,
      additionnalProps: {
        calledFrom,
        expressionType,
        currentWorkflow,
        currentActivity,
        currentList,
        currentTrigger,
        matrixTag,
        bindedInteg,
      },
      activities,
      lists,
      jobs,
      webhooks,

      numberIndex,
      mobileProfiles,
      currentStep,
      useDefaultFields,
    } = this.props;

    const shouldMarkError = (field: any) => {
      const hasError = errors[field];
      return hasError;
    };

    const labelToDisplay =
      lang.containers.workflows.subCategories.metaExpressions.createEditModal;

    const includeCustomerScope = activityNeedsCustomer(currentActivity);

    const currentResourceId = getResourceIdForScope(scope, {
      currentStepId,
      currentActivity,
      currentList,
      currentTrigger,
    });

    return (
      <Grid container spacing={0}>
        <Grid item={true} xs={4}>
          {this.buildStepper()}
        </Grid>

        <Grid item={true} xs={1}>
          <Divider orientation="vertical" />
        </Grid>
        <Grid item={true} xs={7}>
          <br />

          {currentStep === 1 && (
            <MetaExpressionWizardFirstStep
              scope={scope}
              field={field}
              shouldMarkError={shouldMarkError}
              labelToDisplay={labelToDisplay}
              title={title ?? ""}
              target_type={target_type}
              target_content={target_content}
              calledFrom={calledFrom}
              includeCustomerScope={includeCustomerScope}
              includeIntegRecordScope={bindedInteg ? true : false}
              includeTriggerEventScope={Boolean(currentTrigger)}
              onChange={this.handleInputChange}
              sqlQueryStatus={{
                disabled,
                verified,
              }}
            />
          )}

          {currentStep === 2 && (
            <MetaExpressionWizardSecondStep
              step={step}
              currentWorkflow={currentWorkflow}
              shouldMarkError={shouldMarkError}
              labelToDisplay={labelToDisplay}
              onChange={this.handleInputChange}
            />
          )}

          {currentStep === 3 && (
            <MetaExpressionWizardThirdStep
              step={step}
              scope={currentStepId && subscope ? subscope : scope}
              subscope={subscope}
              calledFrom={calledFrom}
              useDefaultFields={useDefaultFields}
              useSecondDefaultFields={useSecondDefaultFields}
              currentResourceId={currentResourceId}
              mobileUserRoleOptions={(mobileProfiles ?? []).map((m) => ({
                key: m.name,
                label: m.name,
              }))}
              // Use current resource if not in store, for resources in create mode.
              activities={mergeCurrentResource(activities, currentActivity)}
              lists={mergeCurrentResource(lists, currentList)}
              webhooks={mergeCurrentResource(webhooks, currentTrigger)}
              transformations={
                getJobsFromType(
                  jobs,
                  STEP_TYPE.TRANSFORMATION,
                ) as ITransformation[]
              }
              numberIndex={numberIndex}
              expressionType={expressionType}
              custom_field1={custom_field1}
              custom_field2={custom_field2}
              custom_field3={custom_field3}
              option_tag={option_tag}
              field={field}
              shouldMarkError={shouldMarkError}
              labelToDisplay={labelToDisplay}
              // calledFromMatrixTag should only have a value if we chose MATRIX_CELL scope
              calledFromMatrixTag={
                calledFrom === CALLED_FROM.MATRIX_CELL ||
                scope === META_EXPRESSION_SCOPE.MATRIX_CELL ||
                scope === META_EXPRESSION_SCOPE.ITEM_CELL
                  ? matrixTag
                  : undefined
              }
              onChange={this.handleInputChange}
              type={this.props.additionnalProps.type}
              triggerEvent={currentTrigger?.event_type}
            />
          )}

          {currentStep === 4 && (
            <MetaExpressionWizardFourthStep
              defaultMEValue={default_value}
              onChangeDefaultMEValue={this.onChangeDefaultMEValue}
            />
          )}
        </Grid>
      </Grid>
    );
  }
}

function mapStateToProps(state: IRootState) {
  return {
    activities: getAllActivities(state), // we read the activities directly from the store. /!\ the "currentActivity" needs to be replaced!
    lists: allListsSelector(state), // we read the lists directly from the store. /!\ the "currentList" needs to be replaced!
    jobs: allJobsComposedSelector(state), // we read the transformations directly from the store.
    webhooks: getAllWebhooks(state),
    currentClient: getSelectedClient(state),
    lang: l[getLang(state)],
    mobileProfiles: getMobileProfiles(state),
  };
}

/* istanbul ignore next */
export default withStyles(styles as any)(
  connect(mapStateToProps)(MetaExpressionWizard),
);
